const evidenceProjectCodeXorKey = 0x1a3;
const maxEvidenceProjectId = 0xfff;
const maxEvidenceNumber = 9999;

function assertIntegerInRange(value: number, min: number, max: number, name: string) {
  if (!Number.isInteger(value) || value < min || value > max) {
    throw new RangeError(`${name} must be an integer between ${min} and ${max}. Got: ${value}`);
  }
}

export function encodeEvidenceProjectId(projectId: number) {
  assertIntegerInRange(projectId, 0, maxEvidenceProjectId, "projectId");

  return (projectId ^ evidenceProjectCodeXorKey).toString(16).padStart(3, "0").toUpperCase();
}

export function decodeEvidenceProjectPrefix(prefix: string) {
  if (!/^[0-9A-Fa-f]{3}$/.test(prefix)) {
    throw new Error(`prefix must be exactly 3 hexadecimal characters. Got: ${prefix}`);
  }

  return Number.parseInt(prefix, 16) ^ evidenceProjectCodeXorKey;
}

function createProjectScopedCode(
  typeMarker: "E" | "R" | "I",
  itemNumber: number,
  projectNumber = 1,
  itemLabel: "evidenceNumber" | "recommendationNumber" | "issueNumber"
) {
  const normalizedProjectNumber = Math.trunc(projectNumber);
  const normalizedItemNumber = Math.trunc(itemNumber);

  assertIntegerInRange(normalizedProjectNumber, 0, maxEvidenceProjectId, "projectNumber");
  assertIntegerInRange(normalizedItemNumber, 1, maxEvidenceNumber, itemLabel);

  return `${encodeEvidenceProjectId(normalizedProjectNumber)}${typeMarker}${String(
    normalizedItemNumber
  ).padStart(4, "0")}`;
}

export function createEvidenceCode(evidenceNumber: number, projectNumber = 1) {
  return createProjectScopedCode("E", evidenceNumber, projectNumber, "evidenceNumber");
}

export function createRecommendationCode(recommendationNumber: number, projectNumber = 1) {
  return createProjectScopedCode("R", recommendationNumber, projectNumber, "recommendationNumber");
}

export function createIssueCode(issueNumber: number, projectNumber = 1) {
  return createProjectScopedCode("I", issueNumber, projectNumber, "issueNumber");
}

export function getEvidenceNumberFromCode(code: string) {
  const evidenceNumber = Number.parseInt(code.slice(-4), 10);

  return Number.isFinite(evidenceNumber) ? evidenceNumber : 0;
}

export function getEvidenceProjectNumber(projectLabel: string) {
  const projectNumber = Number(projectLabel.trim().split("-").at(-1));

  return Number.isFinite(projectNumber) && projectNumber > 0 ? projectNumber : 1;
}
